import { useMemo, useState, useEffect } from 'react';
import { subscribeGuides } from '@/data/guidesRepo';
import type { GuideType } from '@/types/types_guide';

/** Subscribes to the whole guide roster in real time, in display order. */
export function useGuides() {
  const [guides, setGuides] = useState<GuideType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return subscribeGuides((list) => {
      setGuides(list);
      setLoading(false);
    });
  }, []);

  return { guides, loading };
}

/**
 * The roster as the public site sees it: active guides only.
 *
 * Derived from the same subscription rather than a filtered query, because the
 * admin list needs the inactive ones and a second listener on a collection this
 * small would cost more than the filter does.
 */
export function useActiveGuides() {
  const { guides, loading } = useGuides();
  const activeGuides = useMemo(() => guides.filter((g) => g.status === 'active'), [guides]);
  return { guides: activeGuides, loading };
}

/** A single guide by id, derived from the subscribed roster. */
export function useGuide(id: string | undefined) {
  const { guides, loading } = useGuides();
  const guide = id ? guides.find((g) => g.id === id) : undefined;
  return { guide, loading };
}
